/**
 * AI Configuration Service
 * 
 * Handles all AI configuration related API calls (admin only).
 */

import api from './api';
import type {
  AIConfig,
  UpdateAIConfigRequest,
  ApiResponse,
  CategorySuggestionsResponse,
  ColumnSuggestionsResponse,
  PaymentMethodSuggestionsResponse,
} from '@budgira/shared';

export const aiConfigService = {
  /**
   * Get the current AI configuration
   */
  async get(): Promise<ApiResponse<AIConfig>> {
    const response = await api.get<ApiResponse<AIConfig>>('/admin/ai-config');
    return response.data;
  },

  /**
   * Update the AI configuration
   */
  async update(data: UpdateAIConfigRequest): Promise<ApiResponse<AIConfig>> {
    const response = await api.put<ApiResponse<AIConfig>>('/admin/ai-config', data);
    return response.data;
  },

  /**
   * Test the AI connection with provided config
   */
  async testConnection(config?: {
    provider: string;
    apiKey?: string;
    apiEndpoint?: string;
    model?: string;
  }): Promise<ApiResponse<{ success: boolean; message: string }>> {
    const response = await api.post<ApiResponse<{ success: boolean; message: string }>>('/admin/ai-config/test', config || {});
    return response.data;
  },

  /**
   * Get category suggestions for import mapping
   */
  async getCategorySuggestions(sourceCategories: string[]): Promise<ApiResponse<CategorySuggestionsResponse>> {
    const response = await api.post<ApiResponse<CategorySuggestionsResponse>>('/import/category-suggestions', {
      sourceCategories,
    });
    return response.data;
  },

  /**
   * Get column suggestions for import mapping
   */
  async getColumnSuggestions(sourceColumns: string[]): Promise<ApiResponse<ColumnSuggestionsResponse>> {
    const response = await api.post<ApiResponse<ColumnSuggestionsResponse>>('/import/column-suggestions', {
      sourceColumns,
    });
    return response.data;
  },

  /**
   * Get payment method suggestions for import mapping
   */
  async getPaymentMethodSuggestions(sourcePaymentMethods: string[]): Promise<ApiResponse<PaymentMethodSuggestionsResponse>> {
    const response = await api.post<ApiResponse<PaymentMethodSuggestionsResponse>>('/import/payment-method-suggestions', {
      sourcePaymentMethods,
    });
    return response.data;
  },
};

export default aiConfigService;
